import {useEffect, useMemo, useRef, useState} from "react";
import {useDrop} from "react-dnd";
import {useIntegrator} from "../../integrator.context";
import {useApp} from "../../app.context";
import {TechEdgePoint} from "../../models/remoteConstants.model";
import {AddTechDependencyCommand} from "../../commands/techDependency.command";
import {AddTechUnlockCommand} from "../../commands/addTechUnlock.command";
import {RemoveTechUnlockCommand} from "../../commands/removeTechUnlock.command";
import {SetTechNodePositionCommand} from "../../commands/setTechNodePosition.command";
import {SetTechEdgeRouteCommand} from "../../commands/setTechEdgeRoute.command";
import {BatchCommand} from "../../commands/batch.command";
import {
    CELL,
    cellKey,
    cellToWorld,
    clampZoom,
    DEFAULT_VIEW,
    NAME_FONT_RATIO,
    nameCharBudget,
    NODE_R,
    nodeDetail,
    snapToCell,
    ViewTransform,
    ZOOM_PER_PINCH,
    ZOOM_PER_WHEEL,
} from "../../utils/graphCanvas";
import {buildTechGraph, TechGraph, TechGraphNode} from "./techLayout";
import {Axis, insertIndexAt, orthArrow, orthPath} from "./edgeRouting";
import {resolveUnlockImageKey} from "./techUnlock";

const VIEW_STORAGE_KEY = 'rift.editor.techTreeView';
// Pointer travel before a press becomes a drag rather than a click, so
// selecting a node doesn't nudge it (mirrors the Workshop's click slop).
const CLICK_SLOP_PX = 4;
// How close, in screen px, a click must be to an edge to hit it.
const EDGE_PICK_PX = 8;
const HANDLE_R_PX = 6;

type Gesture =
    | {kind: 'pan'; startX: number; startY: number; view0: ViewTransform}
    // Moving a node. `moved` flips once the pointer clears the click slop.
    | {kind: 'node'; id: string; startCell: {x: number; y: number}; grabDX: number; grabDY: number; moved: boolean}
    // Shift+drag between two nodes to toggle a dependency.
    | {kind: 'link'; from: string}
    // Dragging one bend of an edge. `startAxis` is what Alt flips against.
    // `inserted` marks a bend created by this very gesture, which is discarded
    // again if the pointer never moved — so a click on a selected line doesn't
    // silently add a bend.
    | {kind: 'waypoint'; parent: string; child: string; index: number; startAxis: Axis; inserted: boolean; moved: boolean};

interface DragGhost {
    id: string;
    x: number;
    y: number;
    // false when another node already occupies the target cell, in which case
    // releasing dispatches nothing and the node snaps back.
    valid: boolean;
}

interface LinkGhost {
    from: string;
    wx: number;
    wy: number;
    overId?: string;
}

interface EdgeRef {
    parent: string;
    child: string;
}

// The working copy of an edge's bends while one is being dragged. The renderer
// prefers this over the model so the polyline follows the pointer without any
// command dispatch.
interface RouteDraft extends EdgeRef {
    points: TechEdgePoint[];
    endAxis?: Axis;
}

function loadView(): ViewTransform {
    try {
        const raw = localStorage.getItem(VIEW_STORAGE_KEY);
        if (raw) {
            const v = JSON.parse(raw);
            if (typeof v?.centerX === 'number' && typeof v?.centerY === 'number' && typeof v?.zoom === 'number')
                return {centerX: v.centerX, centerY: v.centerY, zoom: clampZoom(v.zoom)};
        }
    } catch {
        // Corrupt or unreadable — fall through to the default view.
    }
    return DEFAULT_VIEW;
}

function TechTreeCanvas(props: {selected?: string; onSelect(id: string | undefined): void}) {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue, getImageUrl} = useApp();
    const svgRef = useRef<SVGSVGElement>(null);
    const wrapRef = useRef<HTMLDivElement | null>(null);

    // executeCommand closes over the live state object, so a long-lived
    // listener must call the latest one rather than the one captured on mount.
    const execRef = useRef(executeCommand);
    execRef.current = executeCommand;

    const [view, setView] = useState<ViewTransform>(loadView);
    // Handlers read the view through a ref so they don't need re-subscribing on
    // every zoom/pan (same reason mapCanvas keeps a viewRef).
    const viewRef = useRef(view);
    viewRef.current = view;

    const [size, setSize] = useState({w: 0, h: 0});
    const gestureRef = useRef<Gesture | undefined>(undefined);
    const [cursor, setCursor] = useState<string>('default');

    // In-flight previews. Local state only — dispatching a command per
    // pointer-move would snapshot the whole model on every frame.
    //
    // Each is mirrored into a ref because pointerup can arrive in the same tick
    // as the last pointermove, before React has re-rendered: the commit path
    // must read the ref or it would save the second-to-last position.
    const [drag, setDrag] = useState<DragGhost | undefined>(undefined);
    const dragRef = useRef<DragGhost | undefined>(undefined);
    const [link, setLink] = useState<LinkGhost | undefined>(undefined);
    const linkRef = useRef<LinkGhost | undefined>(undefined);
    const [draft, setDraft] = useState<RouteDraft | undefined>(undefined);
    const draftRef = useRef<RouteDraft | undefined>(undefined);
    const [selectedEdge, setSelectedEdge] = useState<EdgeRef | undefined>(undefined);
    const selectedEdgeRef = useRef<EdgeRef | undefined>(undefined);

    function showDrag(next: DragGhost | undefined) {
        dragRef.current = next;
        setDrag(next);
    }

    function showLink(next: LinkGhost | undefined) {
        linkRef.current = next;
        setLink(next);
    }

    function showDraft(next: RouteDraft | undefined) {
        draftRef.current = next;
        setDraft(next);
    }

    function selectEdge(next: EdgeRef | undefined) {
        selectedEdgeRef.current = next;
        setSelectedEdge(next);
        if (next) props.onSelect(undefined);
    }

    // Keyed on `state`, NOT `state.techTree`. Commands mutate the model in place
    // and executeCommand only shallow-copies the root object to change its
    // identity, so the techTree array reference survives an edit unchanged —
    // memoising on it would serve stale positions forever (a dragged node would
    // appear to snap back even though its move had been saved). The root object
    // is new after every command and after undo/redo, so this recomputes exactly
    // when something could have changed.
    const graph = useMemo(() => buildTechGraph(state.techTree), [state]);
    const unplaced = graph.nodes.filter(n => !n.placed).length;

    function persistView(next: ViewTransform) {
        setView(next);
        localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(next));
    }

    // The pane is display:none while another tab is active, so this measures
    // 0x0 until it's revealed — re-measure rather than assuming a first paint.
    // Observes the wrapper div, not the SVG: clientWidth/Height on an SVG
    // element is far less consistently supported.
    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const observer = new ResizeObserver(() => {
            setSize({w: wrap.clientWidth, h: wrap.clientHeight});
        });
        observer.observe(wrap);
        return () => observer.disconnect();
    }, []);

    // Wheel zoom anchored on the cursor. Native non-passive listener because
    // React's onWheel can't reliably preventDefault the page scroll.
    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        function onWheel(e: WheelEvent) {
            e.preventDefault();
            const v = viewRef.current;
            // A trackpad pinch arrives as a wheel event with ctrlKey set and
            // much smaller deltas, so it needs its own factor.
            const perDelta = e.ctrlKey ? ZOOM_PER_PINCH : ZOOM_PER_WHEEL;
            const zoom = clampZoom(v.zoom * Math.exp(-e.deltaY * perDelta));
            if (zoom === v.zoom) return;
            const rect = svg!.getBoundingClientRect();
            // Anchor offset from the canvas centre, which is where the view
            // transform is centred — keeps the point under the cursor fixed.
            const ax = e.clientX - rect.left - rect.width / 2;
            const ay = e.clientY - rect.top - rect.height / 2;
            const wx = ax / v.zoom + v.centerX;
            const wy = ay / v.zoom + v.centerY;
            persistView({zoom, centerX: wx - ax / zoom, centerY: wy - ay / zoom});
        }
        svg.addEventListener('wheel', onWheel, {passive: false});
        return () => svg.removeEventListener('wheel', onWheel);
    }, []);

    // Delete straightens the selected edge (drops all its bends). Gated on the
    // pane actually being visible, since every tab stays mounted, and ignored
    // while a text field has focus.
    useEffect(() => {
        function onKeyDown(e: KeyboardEvent) {
            if (e.key !== 'Delete' && e.key !== 'Backspace') return;
            if (!wrapRef.current || wrapRef.current.offsetParent === null) return;
            const target = e.target as HTMLElement | null;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
            const edge = selectedEdgeRef.current;
            if (!edge) return;
            e.preventDefault();
            execRef.current(new SetTechEdgeRouteCommand(edge.parent, edge.child, []));
        }
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    // undefined before mount, and while react-dnd's collect runs against a
    // canvas that isn't laid out yet.
    function screenToWorld(clientX: number, clientY: number): [number, number] | undefined {
        const svg = svgRef.current;
        if (!svg) return undefined;
        const rect = svg.getBoundingClientRect();
        const v = viewRef.current;
        return [
            (clientX - rect.left - rect.width / 2) / v.zoom + v.centerX,
            (clientY - rect.top - rect.height / 2) / v.zoom + v.centerY,
        ];
    }

    // Inside a pointer handler the SVG is necessarily mounted, so the fallback
    // is unreachable — it exists only to keep the handlers free of null checks.
    function pointerWorld(e: {clientX: number; clientY: number}): [number, number] {
        return screenToWorld(e.clientX, e.clientY) ?? [0, 0];
    }

    function nodeAt(clientX: number, clientY: number): TechGraphNode | undefined {
        const world = screenToWorld(clientX, clientY);
        if (!world) return undefined;
        const [wx, wy] = world;
        for (const node of graph.nodes) {
            const [nx, ny] = cellToWorld(node.x, node.y);
            if ((wx - nx) ** 2 + (wy - ny) ** 2 <= NODE_R * NODE_R) return node;
        }
        return undefined;
    }

    // An edge under the pointer, with the index a new bend would take. Hit
    // tested mathematically like the nodes, so picking behaves identically at
    // every zoom without needing an invisible fat path per edge. First hit
    // wins, which only matters where two edges overlap within the tolerance.
    function edgeAt(clientX: number, clientY: number): {edge: EdgeRef; index: number; point: {x: number; y: number}} | undefined {
        const world = screenToWorld(clientX, clientY);
        if (!world) return undefined;
        const [wx, wy] = world;
        const tolerance = EDGE_PICK_PX / viewRef.current.zoom;
        for (const edge of graph.edges) {
            const from = graph.byId.get(edge.parent);
            const to = graph.byId.get(edge.child);
            if (!from || !to) continue;
            const route = routeOf(graph, draftRef.current, edge.parent, edge.child);
            const [fx, fy] = cellToWorld(from.x, from.y);
            const [tx, ty] = cellToWorld(to.x, to.y);
            const hit = insertIndexAt(
                {x: wx, y: wy}, {x: fx, y: fy}, route?.points, {x: tx, y: ty}, route?.endAxis, tolerance);
            if (hit) return {edge, index: hit.index, point: hit.point};
        }
        return undefined;
    }

    // ---- gestures -----------------------------------------------------------

    function onPointerDown(e: React.PointerEvent<SVGSVGElement>) {
        // RMB pans, matching the Map Editor.
        if (e.button === 2) {
            e.currentTarget.setPointerCapture(e.pointerId);
            gestureRef.current = {kind: 'pan', startX: e.clientX, startY: e.clientY, view0: viewRef.current};
            setCursor('grabbing');
            return;
        }
        if (e.button !== 0) return;

        const node = nodeAt(e.clientX, e.clientY);

        // Shift+drag draws a dependency from this node to the one released
        // over. Root can't be either end: buildTechGraph leaves it out of the
        // graph, so nodeAt never returns it — its children are added with the
        // inspector's + button instead.
        if (node && e.shiftKey) {
            e.currentTarget.setPointerCapture(e.pointerId);
            gestureRef.current = {kind: 'link', from: node.tech.id};
            const [wx, wy] = pointerWorld(e);
            showLink({from: node.tech.id, wx, wy});
            return;
        }

        if (node) {
            const [wx, wy] = pointerWorld(e);
            const [nx, ny] = cellToWorld(node.x, node.y);
            e.currentTarget.setPointerCapture(e.pointerId);
            gestureRef.current = {
                kind: 'node',
                id: node.tech.id,
                startCell: {x: node.x, y: node.y},
                // Grab offset, so the node keeps its position relative to the
                // cursor instead of snapping its centre to the pointer.
                grabDX: wx - nx,
                grabDY: wy - ny,
                moved: false,
            };
            return;
        }

        const hit = edgeAt(e.clientX, e.clientY);
        if (hit) {
            const selected = selectedEdgeRef.current;
            const isSelected = selected?.parent === hit.edge.parent && selected?.child === hit.edge.child;
            selectEdge(hit.edge);
            // First click selects; dragging an already-selected edge bends it,
            // so a click never moves a line the designer was only pointing at.
            if (!isSelected) return;

            const route = routeOf(graph, undefined, hit.edge.parent, hit.edge.child);
            const cell = snapToCell(hit.point.x, hit.point.y);
            const points = [...(route?.points ?? [])];
            points.splice(hit.index, 0, {x: cell.x, y: cell.y});
            e.currentTarget.setPointerCapture(e.pointerId);
            gestureRef.current = {
                kind: 'waypoint', parent: hit.edge.parent, child: hit.edge.child, index: hit.index,
                startAxis: 'h', inserted: true, moved: false,
            };
            showDraft({...hit.edge, points, endAxis: route?.endAxis});
            return;
        }

        // Empty space: LMB pans and clears both selections.
        e.currentTarget.setPointerCapture(e.pointerId);
        gestureRef.current = {kind: 'pan', startX: e.clientX, startY: e.clientY, view0: viewRef.current};
        setCursor('grabbing');
        props.onSelect(undefined);
        selectEdge(undefined);
    }

    // Pointer-down on an existing bend handle. The handle captures the pointer
    // itself; the events still bubble to the SVG's move/up handlers.
    function onHandleDown(e: React.PointerEvent<SVGCircleElement>, edge: EdgeRef, index: number) {
        if (e.button !== 0) return;
        e.stopPropagation();
        const route = routeOf(graph, undefined, edge.parent, edge.child);
        if (!route) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        gestureRef.current = {
            kind: 'waypoint', parent: edge.parent, child: edge.child, index,
            startAxis: route.points[index]?.axis ?? 'h', inserted: false, moved: false,
        };
        showDraft({...edge, points: [...route.points], endAxis: route.endAxis});
    }

    // Double-click a handle to remove just that bend.
    function onHandleDoubleClick(e: React.MouseEvent<SVGCircleElement>, edge: EdgeRef, index: number) {
        e.stopPropagation();
        const route = routeOf(graph, undefined, edge.parent, edge.child);
        if (!route) return;
        const points = route.points.filter((_, i) => i !== index);
        executeCommand(new SetTechEdgeRouteCommand(edge.parent, edge.child, points, route.endAxis));
    }

    function onPointerMove(e: React.PointerEvent<SVGSVGElement>) {
        const g = gestureRef.current;
        if (!g) return;

        if (g.kind === 'pan') {
            const v = g.view0;
            setView({
                zoom: v.zoom,
                centerX: v.centerX - (e.clientX - g.startX) / v.zoom,
                centerY: v.centerY - (e.clientY - g.startY) / v.zoom,
            });
            return;
        }

        if (g.kind === 'link') {
            const [wx, wy] = pointerWorld(e);
            const over = nodeAt(e.clientX, e.clientY);
            showLink({
                from: g.from,
                wx,
                wy,
                overId: over && over.tech.id !== g.from ? over.tech.id : undefined,
            });
            return;
        }

        if (g.kind === 'waypoint') {
            const current = draftRef.current;
            if (!current) return;
            g.moved = true;
            const [wx, wy] = pointerWorld(e);
            const cell = snapToCell(wx, wy);
            // Alt flips which way this leg turns, relative to what it was when
            // the drag began, so holding Alt is a toggle rather than a mode.
            const axis: Axis = e.altKey ? (g.startAxis === 'h' ? 'v' : 'h') : g.startAxis;
            const points = current.points.map((p, i) =>
                i === g.index ? {x: cell.x, y: cell.y, ...(axis === 'h' ? {} : {axis})} : p);
            showDraft({...current, points});
            return;
        }

        const [wx, wy] = pointerWorld(e);
        const cell = snapToCell(wx - g.grabDX, wy - g.grabDY);
        if (!g.moved) {
            const [sx, sy] = cellToWorld(g.startCell.x, g.startCell.y);
            const travel = Math.hypot(wx - g.grabDX - sx, wy - g.grabDY - sy) * viewRef.current.zoom;
            if (travel < CLICK_SLOP_PX) return;
            g.moved = true;
        }

        // A cell is blocked only by a *different* node — dragging back over
        // your own starting cell is always fine.
        const occupant = graph.occupied.get(cellKey(cell.x, cell.y));
        showDrag({id: g.id, x: cell.x, y: cell.y, valid: occupant === undefined || occupant === g.id});
    }

    function onPointerEnd() {
        const g = gestureRef.current;
        if (!g) return;
        gestureRef.current = undefined;
        setCursor('default');

        if (g.kind === 'pan') {
            persistView(viewRef.current);
            return;
        }

        if (g.kind === 'link') {
            const target = linkRef.current?.overId;
            showLink(undefined);
            // from = the prerequisite (parent), target = what it unlocks.
            // AddTechDependencyCommand is a toggle, so repeating the drag
            // removes the dependency again.
            if (target) executeCommand(new AddTechDependencyCommand(target, g.from));
            return;
        }

        if (g.kind === 'waypoint') {
            const committed = draftRef.current;
            showDraft(undefined);
            // A press-and-release that never moved on a selected line is just a
            // click: throw the speculative bend away rather than saving it.
            if (committed && !(g.inserted && !g.moved))
                executeCommand(new SetTechEdgeRouteCommand(g.parent, g.child, committed.points, committed.endAxis));
            return;
        }

        const ghost = dragRef.current;
        showDrag(undefined);
        if (!g.moved) {
            // A press without travel is a selection, not a move.
            props.onSelect(g.id);
            selectEdge(undefined);
            return;
        }
        // An invalid drop dispatches nothing, so the node simply reverts to
        // where it started once the ghost clears.
        if (ghost && ghost.valid) {
            executeCommand(new SetTechNodePositionCommand(g.id, ghost.x, ghost.y));
        }
    }

    // Palette entity dropped on the canvas: assign it as an unlock to whichever
    // node is under the cursor. One drop target on the whole canvas rather than
    // per-node, because HTML5 drag sources/targets on SVG elements are not
    // reliable across browsers — the node is resolved from the drop position.
    const [{dropNodeId}, drop] = useDrop({
        accept: 'entity',
        drop: (item: any, monitor) => {
            const offset = monitor.getClientOffset();
            const node = offset && nodeAt(offset.x, offset.y);
            if (!node) return;
            executeCommand(new RemoveTechUnlockCommand(item.nodeId, item.id));
            executeCommand(new AddTechUnlockCommand(node.tech.id, item.id));
        },
        collect: (monitor) => {
            const offset = monitor.isOver({shallow: true}) ? monitor.getClientOffset() : undefined;
            return {dropNodeId: offset ? nodeAt(offset.x, offset.y)?.tech.id : undefined};
        },
    });

    // ---- toolbar actions ----------------------------------------------------

    function zoomToFit() {
        if (graph.nodes.length === 0 || size.w === 0) return;
        const pad = NODE_R * 1.5;
        const minX = Math.min(...graph.nodes.map(n => n.x)) * CELL - pad;
        const maxX = Math.max(...graph.nodes.map(n => n.x)) * CELL + pad;
        const minY = Math.min(...graph.nodes.map(n => n.y)) * CELL - pad;
        const maxY = Math.max(...graph.nodes.map(n => n.y)) * CELL + pad;
        const zoom = clampZoom(Math.min(size.w / (maxX - minX), size.h / (maxY - minY)));
        persistView({zoom, centerX: (minX + maxX) / 2, centerY: (minY + maxY) / 2});
    }

    // Commit the computed fallback position of every node that has never been
    // placed, as a single undo entry. Already-placed nodes are untouched —
    // their command returns false and adds nothing.
    function placeUnplaced() {
        const commands = graph.nodes
            .filter(n => !n.placed)
            .map(n => new SetTechNodePositionCommand(n.tech.id, n.x, n.y));
        if (commands.length > 0) executeCommand(new BatchCommand(commands));
    }

    // ---- render -------------------------------------------------------------

    const {zoom, centerX, centerY} = view;
    const detail = nodeDetail(zoom);
    // Grid only once cells are big enough on screen to read as a grid.
    const showGrid = CELL * zoom >= 24;
    const selectedRoute = selectedEdge
        ? routeOf(graph, draft, selectedEdge.parent, selectedEdge.child)
        : undefined;

    return (
        <div ref={node => {
            wrapRef.current = node;
            drop(node);
        }} style={{position: 'absolute', inset: 0}}>
            <svg ref={svgRef}
                 width={'100%'}
                 height={'100%'}
                 style={{display: 'block', touchAction: 'none', cursor}}
                 onPointerDown={onPointerDown}
                 onPointerMove={onPointerMove}
                 onPointerUp={onPointerEnd}
                 onPointerCancel={onPointerEnd}
                 onContextMenu={e => e.preventDefault()}>
                <g transform={`translate(${size.w / 2},${size.h / 2}) scale(${zoom}) translate(${-centerX},${-centerY})`}>
                    {showGrid && <Grid view={view} size={size}/>}

                    {/* Edges first: they run centre to centre and are hidden
                        under the opaque node circles at both ends. */}
                    {graph.edges.map(edge => {
                        const from = graph.byId.get(edge.parent);
                        const to = graph.byId.get(edge.child);
                        if (!from || !to) return null;
                        const route = routeOf(graph, draft, edge.parent, edge.child);
                        const [fx, fy] = cellToWorld(...dragged(from, drag));
                        const [tx, ty] = cellToWorld(...dragged(to, drag));
                        const isSelected = selectedEdge?.parent === edge.parent && selectedEdge?.child === edge.child;
                        const colour = isSelected ? 'var(--tint-color)' : 'var(--graph-line-color)';
                        const arrow = orthArrow({x: fx, y: fy}, route?.points, {x: tx, y: ty}, route?.endAxis, NODE_R);
                        return (
                            <g key={`${edge.parent}>${edge.child}`}>
                                <path d={orthPath({x: fx, y: fy}, route?.points, {x: tx, y: ty}, route?.endAxis, NODE_R)}
                                      fill={'none'}
                                      stroke={colour}
                                      strokeWidth={isSelected ? 5 : 3}
                                      strokeLinejoin={'round'}
                                      strokeLinecap={'round'}/>
                                {/* Direction tick, pointing into the child. */}
                                {arrow && <polygon points={arrow} fill={colour}/>}
                            </g>
                        );
                    })}

                    {link && <LinkPreview graph={graph} link={link}/>}

                    {graph.nodes.map(node => (
                        <Node key={node.tech.id}
                              node={node}
                              detail={detail}
                              selected={node.tech.id === props.selected}
                              highlight={node.tech.id === dropNodeId || node.tech.id === link?.overId}
                              ghost={drag?.id === node.tech.id ? drag : undefined}
                              name={getLocalizationValue(node.tech.id)}
                              imageUrl={getImageUrl}
                              unlockKey={id => resolveUnlockImageKey(state, id)}/>
                    ))}

                    {/* Bend handles last, so they sit above nodes and stay
                        grabbable where an edge passes behind one. */}
                    {selectedEdge && (selectedRoute?.points ?? []).map((point, i) => {
                        const [hx, hy] = cellToWorld(point.x, point.y);
                        return (
                            <circle key={i}
                                    cx={hx} cy={hy}
                                    r={HANDLE_R_PX / zoom}
                                    fill={'var(--tint-color)'}
                                    stroke={'black'}
                                    strokeWidth={1 / zoom}
                                    style={{cursor: 'move'}}
                                    onPointerDown={e => onHandleDown(e, selectedEdge, i)}
                                    onDoubleClick={e => onHandleDoubleClick(e, selectedEdge, i)}/>
                        );
                    })}
                </g>
            </svg>

            <div className={'layout horizontal gap'} style={{position: 'absolute', top: 8, left: 8}}>
                <button onClick={zoomToFit} title={'Fit the whole tree in view'}>
                    <span className={'fa-solid fa-expand'}/>
                </button>
                {unplaced > 0 &&
                    <button onClick={placeUnplaced}
                            title={`Save the computed position of ${unplaced} node(s) that have never been placed`}>
                        <span className={'fa-solid fa-wand-magic-sparkles'}/> Place {unplaced}
                    </button>}
            </div>

            {selectedEdge &&
                <p style={{position: 'absolute', bottom: 8, left: 8, margin: 0, fontSize: 12, color: 'var(--muted-text-color)'}}>
                    Drag the line to add a bend · double-click a handle to remove it · Alt flips the corner · Delete straightens
                </p>}

            {/* Root isn't drawn, so a tree holding nothing but root is an empty
                canvas — point at the one control that can seed it. */}
            {graph.nodes.length === 0 &&
                <p style={{position: 'absolute', top: '50%', left: 0, right: 0, textAlign: 'center', color: 'var(--muted-text-color)'}}>
                    No tech nodes. Add one with + in the Root details.
                </p>}
        </div>
    );
}

// The route to draw for an edge: the in-flight draft when one is being dragged,
// otherwise whatever the model holds.
function routeOf(
    graph: TechGraph,
    draft: RouteDraft | undefined,
    parent: string,
    child: string,
): {points: TechEdgePoint[]; endAxis?: Axis} | undefined {
    if (draft && draft.parent === parent && draft.child === child)
        return {points: draft.points, endAxis: draft.endAxis};
    const route = graph.byId.get(parent)?.tech.routes?.find(r => r.child === child);
    return route ? {points: route.points ?? [], endAxis: route.endAxis} : undefined;
}

// A node's cell, substituting the in-flight drag position so its edges follow
// the ghost rather than snapping at the end of the drag.
function dragged(node: TechGraphNode, drag?: DragGhost): [number, number] {
    return drag && drag.id === node.tech.id ? [drag.x, drag.y] : [node.x, node.y];
}

function Grid(props: {view: ViewTransform; size: {w: number; h: number}}) {
    const {centerX, centerY, zoom} = props.view;
    const {w, h} = props.size;
    // Only emit the lines actually on screen; a fixed world extent would draw
    // thousands when zoomed out.
    const halfW = w / 2 / zoom;
    const halfH = h / 2 / zoom;
    const minX = Math.floor((centerX - halfW) / CELL);
    const maxX = Math.ceil((centerX + halfW) / CELL);
    const minY = Math.floor((centerY - halfH) / CELL);
    const maxY = Math.ceil((centerY + halfH) / CELL);

    const lines = [];
    for (let x = minX; x <= maxX; x++) {
        lines.push(<line key={`v${x}`} x1={x * CELL} y1={minY * CELL} x2={x * CELL} y2={maxY * CELL}/>);
    }
    for (let y = minY; y <= maxY; y++) {
        lines.push(<line key={`h${y}`} x1={minX * CELL} y1={y * CELL} x2={maxX * CELL} y2={y * CELL}/>);
    }

    return (
        <g stroke={'var(--overlay-color)'} strokeWidth={1 / zoom} opacity={0.25} style={{pointerEvents: 'none'}}>
            {lines}
        </g>
    );
}

function LinkPreview(props: {graph: TechGraph; link: LinkGhost}) {
    const from = props.graph.byId.get(props.link.from);
    if (!from) return null;
    const [fx, fy] = cellToWorld(from.x, from.y);
    const target = props.link.overId ? props.graph.byId.get(props.link.overId) : undefined;
    const [tx, ty] = target ? cellToWorld(target.x, target.y) : [props.link.wx, props.link.wy];
    return (
        <path d={`M ${fx} ${fy} L ${tx} ${ty}`}
              fill={'none'}
              stroke={target ? 'var(--tint-color)' : 'var(--muted-text-color)'}
              strokeWidth={3}
              strokeDasharray={'8 6'}
              style={{pointerEvents: 'none'}}/>
    );
}

function Node(props: {
    node: TechGraphNode;
    detail: ReturnType<typeof nodeDetail>;
    selected: boolean;
    highlight: boolean;
    ghost?: DragGhost;
    name: string;
    imageUrl(id: string): string;
    unlockKey(id: string): string;
}) {
    const {node, detail, ghost} = props;
    const [x, y] = cellToWorld(ghost ? ghost.x : node.x, ghost ? ghost.y : node.y);
    const unlocks = node.tech.unlocks ?? [];

    const stroke = (() => {
        if (ghost && !ghost.valid) return 'var(--error-color)';
        if (props.highlight || props.selected) return 'var(--tint-color)';
        if (node.orphan) return 'var(--error-color)';
        return 'var(--graph-line-color)';
    })();

    // Font and icon sizes are in world units, so contents keep the same
    // proportion of the circle at every zoom; LOD decides what's drawn at all.
    const fontSize = NODE_R * NAME_FONT_RATIO;
    const label = truncate(props.name, nameCharBudget(NODE_R, fontSize));
    // Icons sit in a row below the label. Kept clear of the text and narrow
    // enough that the row still fits inside the circle at that height.
    const iconSize = NODE_R * 0.42;
    const iconTop = NODE_R * 0.2;
    const shown = unlocks.slice(0, 3);

    return (
        <g transform={`translate(${x},${y})`} opacity={ghost ? 0.7 : 1} style={{cursor: 'pointer'}}>
            <circle r={NODE_R}
                    fill={'var(--box-color)'}
                    stroke={stroke}
                    strokeWidth={props.selected ? 4 : 2}
                    // Dashed while a node has no saved position, so it's clear
                    // the layout is computed rather than authored.
                    strokeDasharray={node.placed ? undefined : '6 5'}/>

            {detail === 'marker' && unlocks.length > 0 &&
                <circle cy={NODE_R * 0.35} r={NODE_R * 0.16} fill={'var(--tint-color)'}/>}

            {/* Always on the circle's vertical centre — its widest point, so the
                name gets the most room before it has to be truncated. */}
            {(detail === 'name' || detail === 'full') &&
                <text y={0}
                      textAnchor={'middle'}
                      dominantBaseline={'middle'}
                      fontSize={fontSize}
                      fill={'white'}
                      style={{pointerEvents: 'none', userSelect: 'none'}}>
                    {label}
                </text>}

            {detail === 'full' && shown.map((id, i) => (
                <image key={id}
                       href={props.imageUrl(props.unlockKey(id))}
                       x={(i - (shown.length - 1) / 2) * iconSize * 1.15 - iconSize / 2}
                       y={iconTop}
                       width={iconSize}
                       height={iconSize}
                       preserveAspectRatio={'xMidYMid slice'}
                       style={{pointerEvents: 'none'}}/>
            ))}
        </g>
    );
}

function truncate(value: string, budget: number): string {
    return value.length <= budget ? value : `${value.slice(0, Math.max(1, budget - 1))}…`;
}

export default TechTreeCanvas;
